import { storage } from '@uhg-abyss/web/tools/storage';
import {
  QueryParamsKeys,
  SessionInitStatus,
  StoreKeys,
} from '@/common/constants';
import {
  EmptySSOPortalEligibilityData,
  SSOPortalEligibilityData,
} from 'api/src/dataModels/SSOPortalEligibilityData';
import { useEffect, useState } from 'react';
import { useSessionStorage } from 'usehooks-ts';
import { createSsoSession } from 'api/src/services/createSsoSession';
import { SSO_SESSION_COOKIE_NAME } from 'commonUtils/src/sessionManager';
import {
  INTERNAL_ERROR_PATH,
  UMR_ELIGIBILITY_ERROR_PATH,
} from '../../appConstants';
import { applyUmrLeasedIdentifier } from './applyUmrLeasedIdentifier';
import { config } from '@uhg-abyss/web/tools/config';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { PortalSource } from 'api/src/dataModels/PortalDataEnums';

export const useSessionInit = (
  searchParams,
  router
): keyof typeof SessionInitStatus => {
  const refToken = searchParams?.get('REF');

  // TODO: only allow below params if DEMO feature flag from lagoon is true.
  const portalSource = searchParams?.get(QueryParamsKeys.PORTAL_SOURCE);
  const portalIdentifier = searchParams?.get(QueryParamsKeys.PORTAL_IDENTIFIER);
  const useFakeUser = searchParams?.get('useFakeUser') === 'true';

  const { enableUmrLeasedCheck, umrLeasedExcludedGroups } = useFlags();

  const excludedUmrLeasedPolicyIds: string[] = (umrLeasedExcludedGroups ?? '')
    .split(',')
    .map((policyId: string) => policyId.trim().toLowerCase())
    .filter(Boolean);

  const [ssoPortalEligibilityData, setSsoPortalEligibilityData] =
    useSessionStorage<SSOPortalEligibilityData>(
      StoreKeys.SSO_PORTAL_ELIGIBILITY_DATA,
      EmptySSOPortalEligibilityData
    );

  const [initStatus, setInitStatus] = useState<keyof typeof SessionInitStatus>(
    SessionInitStatus.NOT_INITIALIZED
  );

  useEffect(() => {
    const doCreateSession = async (
      params,
      useFetchUrl = false
    ): Promise<SSOPortalEligibilityData> => {
      try {
        const data: SSOPortalEligibilityData = await createSsoSession(
          params,
          config,
          useFetchUrl
        );

        if (portalSource) data.portalSource = portalSource;
        if (portalIdentifier) data.portalIdentifier = portalIdentifier;

        return data;
      } catch (error) {
        throw error;
      }
    };

    function clearCookie(name: string) {
      const domain = `.${window.location.hostname}`;
      document.cookie = `${name}=; Max-Age=0; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Domain=${domain}; Path=/; Secure`;
    }

    const getEnrichedData = async (
      data: SSOPortalEligibilityData
    ): Promise<SSOPortalEligibilityData> => {
      const isUmr = data.portalSource?.toLowerCase() === PortalSource.UMR;
      const isExcludedPolicyId = excludedUmrLeasedPolicyIds.includes(
        data.policyId?.toLowerCase() ?? ''
      );
      return enableUmrLeasedCheck && isUmr && !isExcludedPolicyId
        ? await applyUmrLeasedIdentifier(data)
        : data;
    };

    const doInit = async () => {
      if (refToken || useFakeUser) {
        const refCodeForStoring = refToken || 'fakeUserToken';

        // check if we already made the init request for the same ref token
        const existingRefToken = storage.session.get(
          StoreKeys.EXISTING_REF_TOKEN
        );
        const needToCreateSession = refCodeForStoring !== existingRefToken;
        if (!needToCreateSession) {
          if (ssoPortalEligibilityData.firstName) {
            setInitStatus(SessionInitStatus.SUCCESS);
          }
        } else {
          setSsoPortalEligibilityData(EmptySSOPortalEligibilityData);
          storage.session.set(StoreKeys.EXISTING_REF_TOKEN, refCodeForStoring);
          clearCookie(SSO_SESSION_COOKIE_NAME);

          let params = {};
          if (refToken) params = { refToken };
          if (useFakeUser) params = { useFakeUser };
          try {
            const data = await doCreateSession(params);
            const enrichedData = await getEnrichedData(data);
            setSsoPortalEligibilityData(enrichedData);
            setInitStatus(SessionInitStatus.SUCCESS);
          } catch (error) {
            if (error?.status >= 400 && error?.status < 600) {
              setInitStatus(SessionInitStatus.ERROR);
              router?.navigate(
                error?.source === 'umrLeasedEligibility'
                  ? UMR_ELIGIBILITY_ERROR_PATH
                  : INTERNAL_ERROR_PATH
              );
            }
          }
        }
      } else {
        if (ssoPortalEligibilityData.firstName) {
          setInitStatus(SessionInitStatus.SUCCESS);
        } else if (storage.cookie.get(SSO_SESSION_COOKIE_NAME)) {
          // If cookie exists, call createSession. If session is still valid, we will get SSO attributes
          try {
            const data = await doCreateSession({}, true);
            const enrichedData = await getEnrichedData(data);
            setSsoPortalEligibilityData(enrichedData);
            setInitStatus(SessionInitStatus.SUCCESS);
          } catch (error) {
            if (error?.status >= 400 && error?.status < 600) {
              setInitStatus(SessionInitStatus.ERROR);
              router?.navigate(
                error?.source === 'umrLeasedEligibility'
                  ? UMR_ELIGIBILITY_ERROR_PATH
                  : INTERNAL_ERROR_PATH
              );
            }
          }
        }
      }
    };

    doInit();
  }, [
    portalIdentifier,
    portalSource,
    refToken,
    setSsoPortalEligibilityData,
    ssoPortalEligibilityData,
    useFakeUser,
    router,
    enableUmrLeasedCheck,
    excludedUmrLeasedPolicyIds,
  ]);

  return initStatus;
};
